import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertTriangle, Filter, Car, Users } from "lucide-react";
import { ExportBar, exportCSV, exportExcel, exportPDF } from "../components/TableExport";

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [debts, setDebts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [recettes, setRecettes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showDebt, setShowDebt] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", pin: "" });
  const [debtForm, setDebtForm] = useState({ type: "amende", montant: "", description: "" });
  const [filterStatut, setFilterStatut] = useState("actif");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [dRes, debRes, aRes, vRes, rRes] = await Promise.all([
        api.get("/users?role=chauffeur"), api.get("/debts"), api.get("/assignments"), api.get("/vehicles"), api.get("/recettes")
      ]);
      setDrivers(dRes.data); setDebts(debRes.data); setAssignments(aRes.data); setVehicles(vRes.data); setRecettes(rRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, role: "chauffeur" };
      if (selectedDriver) {
        await api.put(`/users/${selectedDriver.id}`, payload);
        toast.success("Chauffeur mis a jour");
      } else {
        await api.post("/users", payload);
        toast.success("Chauffeur ajoute");
      }
      setShowAdd(false); setSelectedDriver(null);
      setForm({ nom: "", prenom: "", email: "", telephone: "", pin: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDebtSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/debts", { ...debtForm, chauffeur_id: selectedDriver.id, montant: parseFloat(debtForm.montant) });
      toast.success("Dette enregistree");
      setShowDebt(false); setSelectedDriver(null);
      setDebtForm({ type: "amende", montant: "", description: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handlePayDebt = async (debtId) => {
    try {
      await api.put(`/debts/${debtId}`, { statut: "rembourse", montant_restant: 0 });
      toast.success("Dette marquee comme remboursee");
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const openEdit = (d) => {
    setSelectedDriver(d);
    setForm({ nom: d.nom, prenom: d.prenom, email: d.email || "", telephone: d.telephone || "", pin: "" });
    setShowAdd(true);
  };

  const getDriverDebt = (driverId) => debts.filter(d => d.chauffeur_id === driverId && d.statut === "en_cours").reduce((sum, d) => sum + (d.montant_restant || 0), 0);

  const getDriverRecettes = (driverId) => recettes.filter(r => r.chauffeur_id === driverId).reduce((sum, r) => sum + (r.montant_paye || 0), 0);

  const getDriverJoursTravailles = (driverId) => new Set(recettes.filter(r => r.chauffeur_id === driverId).map(r => r.date_location)).size;

  const getDriverAssignments = (driverId) => assignments.filter(a => a.chauffeur_id === driverId && a.actif);
  const isDriverActive = (driverId) => getDriverAssignments(driverId).length > 0;
  const getDriverVehicles = (driverId) => {
    return getDriverAssignments(driverId).map(a => vehicles.find(v => v.id === a.vehicule_id)).filter(Boolean);
  };

  const filteredDrivers = drivers.filter(d => {
    if (filterStatut === "actif") return isDriverActive(d.id);
    if (filterStatut === "inactif") return !isDriverActive(d.id);
    return true;
  });

  const activeCount = drivers.filter(d => isDriverActive(d.id)).length;
  const inactiveCount = drivers.length - activeCount;

  const driverExportCols = [
    { label: "Prenom", key: "prenom" },
    { label: "Nom", key: "nom" },
    { label: "Telephone", key: "telephone" },
    { label: "Statut", accessor: d => isDriverActive(d.id) ? "Actif" : "Non actif" },
    { label: "Vehicule", accessor: d => getDriverVehicles(d.id).map(v => v.immatriculation).join(", ") || "-" },
    { label: "Dette en cours (Frc)", accessor: d => getDriverDebt(d.id) },
    { label: "Recettes payees (Frc)", accessor: d => getDriverRecettes(d.id) },
    { label: "Jours travailles", accessor: d => getDriverJoursTravailles(d.id) },
  ];

  const debtExportCols = [
    { label: "Chauffeur", accessor: d => { const dr = drivers.find(x => x.id === d.chauffeur_id); return dr ? `${dr.prenom} ${dr.nom}` : "-"; } },
    { label: "Type", key: "type" },
    { label: "Montant (Frc)", key: "montant" },
    { label: "Restant (Frc)", key: "montant_restant" },
    { label: "Description", key: "description" },
    { label: "Statut", accessor: d => d.statut === "en_cours" ? "En cours" : "Rembourse" },
  ];

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="drivers-page">
      <Tabs defaultValue="chauffeurs">
        <div className="page-header">
          <div>
            <h1 className="page-title">Chauffeurs & Dettes</h1>
            <p className="text-sm text-slate-500 mt-1">{drivers.length} chauffeur(s) - {activeCount} actif(s), {inactiveCount} non actif(s)</p>
          </div>
          <div className="flex gap-2">
            <TabsList>
              <TabsTrigger value="chauffeurs" data-testid="tab-drivers">Chauffeurs</TabsTrigger>
              <TabsTrigger value="dettes" data-testid="tab-debts">Dettes</TabsTrigger>
            </TabsList>
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setSelectedDriver(null); setForm({ nom: "", prenom: "", email: "", telephone: "", pin: "" }); setShowAdd(true); }} data-testid="btn-add-driver">
              <Plus className="w-4 h-4 mr-2" />Ajouter
            </Button>
          </div>
        </div>

        <TabsContent value="chauffeurs">
          {/* Filter + Export */}
          <ExportBar
            searchTerm="" onSearchChange={null} searchPlaceholder={null}
            filterConfigs={[{ key: "statut", label: `Tous (${drivers.length})`, options: [{ value: "actif", label: `Actifs (${activeCount})` }, { value: "inactif", label: `Non actifs (${inactiveCount})` }] }]}
            filters={{ statut: filterStatut }} onFilterChange={(k, v) => setFilterStatut(v)} onReset={() => setFilterStatut("all")} hasActiveFilters={filterStatut !== "all"}
            data={filteredDrivers} columns={driverExportCols} filename="chauffeurs" title="Liste des chauffeurs"
          />
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Nom</TableHead>
                  <TableHead className="font-semibold text-slate-600">Telephone</TableHead>
                  <TableHead className="font-semibold text-slate-600">Vehicule affecte</TableHead>
                  <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                  <TableHead className="font-semibold text-slate-600">Recettes payees</TableHead>
                  <TableHead className="font-semibold text-slate-600">Jours travailles</TableHead>
                  <TableHead className="font-semibold text-slate-600">Dette en cours</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDrivers.map(d => {
                  const debt = getDriverDebt(d.id);
                  const active = isDriverActive(d.id);
                  const driverVehicles = getDriverVehicles(d.id);
                  const totalRecettes = getDriverRecettes(d.id);
                  const joursTravailles = getDriverJoursTravailles(d.id);
                  return (
                    <TableRow key={d.id} data-testid={`driver-row-${d.id}`} className={!active ? "opacity-70" : ""}>
                      <TableCell className="font-medium text-slate-900">{d.prenom} {d.nom}</TableCell>
                      <TableCell className="text-slate-600">{d.telephone || "-"}</TableCell>
                      <TableCell>
                        {driverVehicles.length > 0 ? (
                          <div className="space-y-1">
                            {driverVehicles.map(v => (
                              <div key={v.id} className="flex items-center gap-1.5">
                                <Car className="w-3.5 h-3.5 text-[#0F52BA]" />
                                <span className="text-sm font-medium text-slate-800">{v.immatriculation}</span>
                                <span className="text-xs text-slate-400">{v.marque} {v.modele}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">Aucun vehicule</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-300"}>
                          {active ? "Actif" : "Non actif"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-emerald-600 font-medium">{totalRecettes.toLocaleString("fr-FR")} Frc</TableCell>
                      <TableCell className="text-slate-700 font-medium">{joursTravailles} jour(s)</TableCell>
                      <TableCell>{debt > 0 ? <Badge variant="outline" className="status-impaye"><AlertTriangle className="w-3 h-3 mr-1" />{debt} Frc</Badge> : <Badge variant="outline" className="status-paye">A jour</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="text-[#0F52BA]" onClick={() => { setSelectedDriver(d); setShowDebt(true); }} data-testid={`btn-debt-${d.id}`}>+ Dette</Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(d)} data-testid={`btn-edit-driver-${d.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredDrivers.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Aucun chauffeur</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="dettes">
          <ExportBar
            searchTerm="" onSearchChange={null} searchPlaceholder={null}
            filterConfigs={[]} filters={{}} onFilterChange={() => {}} onReset={() => {}} hasActiveFilters={false}
            data={debts} columns={debtExportCols} filename="dettes" title="Liste des dettes"
          />
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Chauffeur</TableHead>
                  <TableHead className="font-semibold text-slate-600">Type</TableHead>
                  <TableHead className="font-semibold text-slate-600">Montant</TableHead>
                  <TableHead className="font-semibold text-slate-600">Restant</TableHead>
                  <TableHead className="font-semibold text-slate-600">Description</TableHead>
                  <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {debts.map(debt => {
                  const driver = drivers.find(d => d.id === debt.chauffeur_id);
                  return (
                    <TableRow key={debt.id} data-testid={`debt-row-${debt.id}`}>
                      <TableCell className="font-medium text-slate-900">{driver ? `${driver.prenom} ${driver.nom}` : "-"}</TableCell>
                      <TableCell><Badge variant="outline">{debt.type}</Badge></TableCell>
                      <TableCell className="text-slate-600">{debt.montant} Frc</TableCell>
                      <TableCell className="font-medium">{debt.montant_restant} Frc</TableCell>
                      <TableCell className="text-slate-600 max-w-[200px] truncate">{debt.description || "-"}</TableCell>
                      <TableCell><Badge variant="outline" className={debt.statut === "en_cours" ? "status-impaye" : "status-paye"}>{debt.statut === "en_cours" ? "En cours" : "Rembourse"}</Badge></TableCell>
                      <TableCell className="text-right">
                        {debt.statut === "en_cours" && (
                          <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handlePayDebt(debt.id)} data-testid={`btn-pay-debt-${debt.id}`}>Marquer paye</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {debts.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Aucune dette</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Driver Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="driver-dialog">
          <DialogHeader><DialogTitle className="font-heading">{selectedDriver ? "Modifier le chauffeur" : "Ajouter un chauffeur"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Prenom</Label><Input value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} required data-testid="input-prenom" /></div>
              <div><Label>Nom</Label><Input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} required data-testid="input-nom" /></div>
              <div><Label>Telephone</Label><Input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} data-testid="input-telephone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="input-email" /></div>
              <div className="col-span-2"><Label>Code PIN</Label><Input type="password" value={form.pin} onChange={e => setForm({...form, pin: e.target.value})} placeholder={selectedDriver ? "Laisser vide pour ne pas changer" : "PIN de connexion"} data-testid="input-pin" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-driver">{selectedDriver ? "Modifier" : "Ajouter"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Debt Dialog */}
      <Dialog open={showDebt} onOpenChange={setShowDebt}>
        <DialogContent data-testid="debt-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ajouter une dette pour {selectedDriver?.prenom} {selectedDriver?.nom}</DialogTitle></DialogHeader>
          <form onSubmit={handleDebtSubmit} className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={debtForm.type} onValueChange={v => setDebtForm({...debtForm, type: v})}>
                <SelectTrigger data-testid="select-debt-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="amende">Amende</SelectItem>
                  <SelectItem value="pret">Pret</SelectItem>
                  <SelectItem value="dette_recette">Dette recette</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Montant (Frc)</Label><Input type="number" value={debtForm.montant} onChange={e => setDebtForm({...debtForm, montant: e.target.value})} required data-testid="input-debt-montant" /></div>
            <div><Label>Description</Label><Input value={debtForm.description} onChange={e => setDebtForm({...debtForm, description: e.target.value})} data-testid="input-debt-desc" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDebt(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-debt">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
